import { Box, Paper, Stack, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";
import SmallButton from "../../Common/SmallButton";
import { UserContext } from "../../UserContext";
import { CustomizeButton } from "./Buttons/CustomizeButton";
import { FormCancel } from "./Buttons/FormCancel";
import { FormEdit } from "./Buttons/FormEdit";
import { FormSave } from "./Buttons/FormSave";
import { MapContent } from "./MapContent";

export const AddressDetail = ({ data, refreshData }) => {
    const debug = false;

    const [editMode, setEditMode] = useState(false);
    const [newCoordinates, setNewCoordinates] = useState([]);
    const [newAddress, setNewAddress] = useState("");
    const [findLocation, setFindLocation] = useState(false);
    const [saving, setSaving] = useState(false);

    const { selectedLocation } = useContext(UserContext);
    const { t } = useTranslation();
    const { ApiCall } = useContext(ApiContext);

    const handleNewCoordinates = (props) => setNewCoordinates(props);
    const handleNewAddress = (props) => setNewAddress(props);

    const cancelAction = () => {
        setNewAddress(data.address);
        setNewCoordinates({
            lat: data.coordinates.lat.toString(),
            lon: data.coordinates.lon.toString()
        });
        setEditMode(false);
    };

    const handleSave = () => {
        setSaving(true);
        ApiCall("PUT", `client/location/${data.id}`, {
            address: newAddress,
            coordinates: {
                lat: newCoordinates.lat.toString(),
                lon: newCoordinates.lon.toString()
            }
        })
            .then(() => {
                setEditMode(false);
                refreshData(data.id);
                setSaving(false);
            })
            .catch((error) => {
                if (debug) console.log(error);
                setSaving(false);
            });
    };

    const resetData = () => {
        setNewAddress(data.address);
        if (
            data.coordinates.lat !== undefined &&
            data.coordinates.lon !== undefined
        )
            setNewCoordinates({
                lat: data.coordinates.lat.toString(),
                lon: data.coordinates.lon.toString()
            });
    };

    useEffect(() => {
        setEditMode(false);
        resetData();
    }, [selectedLocation]);

    return (
        <Paper
			elevation={0}
			sx={{
				padding: 2,
				border: editMode ? `1px dashed ${ColorScheme.secondary}` : "0px",
				borderRadius: 2,
				width: "100%",
				overflow: "hidden",
				boxSizing: "border-box",
				backgroundColor: ColorScheme.transparent // Ensures default MUI background
			}}
		>
            <Stack
				direction={{ xs: "column", sm: "row" }}
				justifyContent="space-between"
				alignItems={{ xs: "stretch", sm: "center" }} // <-- changed here
				spacing={2}
				width="100%"
			>
				<Stack
					direction="column"
					spacing={1}
					sx={{ flex: 1, minWidth: 0 }}
				>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ minWidth: 0 }}
                    >
                        <Typography
                            sx={{ minWidth: isMobile ? 80 : 140, flexShrink: 0, color: ColorScheme.textPrimary }}
                        >
                            {t("location.form_address_label")}:
                        </Typography>
                        {editMode ? (
                           <Stack
								direction={{ xs: "column", sm: "row" }}
								spacing={1}
								flex={1}
								alignItems="center" // <-- add this line
							>
								<FormEdit
									value={newAddress}
									changeData={(text) => {
										if (findLocation) setFindLocation(false);
										setNewAddress(text);
									}}
									isRequired={true}
									fullWidth
								/>
								<SmallButton 
									action={() => setFindLocation(true)}
									backgroundColor={ColorScheme.buttonPrimary}
									text={t("buttons.locate_address")}
									borderColor={ColorScheme.buttonPrimary}
								/>
							</Stack>
                        ) : (
                            <Typography
                                variant="form_text"
                                sx={{
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    flex: 1,
									color: ColorScheme.textPrimary
                                }}
                            >
                                {data.address}
                            </Typography>
                        )}
                    </Stack>
                    <Box sx={{ width: "100%", mt: 1 }}>
                        <MapContent
                            edit={editMode}
                            locateAddress={findLocation && newAddress}
                            newCoordinates={handleNewCoordinates}
                            passAddressNameFromCoordinates={handleNewAddress}
                        />
                    </Box>
                    {editMode && (
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 1
                            }}
                        >
                            <FormCancel disabled={saving} cancelAction={cancelAction} />
                            <FormSave
                                check={newAddress === ""}
                                handleSave={handleSave}
                                activity={saving}
                            />
                        </Box>
                    )}
                </Stack>
                {!editMode && (
                    <Box>
                        <CustomizeButton
                            changeEditMode={() => setEditMode(true)}
                        />
                    </Box>
                )}
            </Stack>
        </Paper>
    );
};